import * as React from 'react';
import * as time from '@tock/shared/utils/time';
import { FormatDateOptions, useIntl } from 'react-intl';
import { InputAdornment, Popover, PopoverProps } from '@mui/material';
import { LocalDate } from 'js-joda';
import {
  datePickerFieldCalendar,
  datePickerFieldPopover,
} from 'ui/DatePickerField/datePickerFieldCssDefs';
import CalendarV5, { Props as CalendarProps } from 'components/calendar/CalendarV5';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TextField, { TextFieldProps } from 'ui/TextField';
import _ from 'lodash';

export interface DatePickerFieldProps
  extends Pick<TextFieldProps, Exclude<keyof TextFieldProps, 'onChange'>> {
  PopoverProps?: Partial<PopoverProps>;
  CalendarProps?: Partial<CalendarProps>;
  onChange?(date: LocalDate): void;
  format?: FormatDateOptions;
}

/**
 * Use `DatePicker` for rendering a text field with a calendar popover
 *
 * ```js
 * import { DatePickerField } from 'ui';
 * ```
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const DatePickerField: React.FunctionComponent<React.PropsWithChildren<DatePickerFieldProps>> = ({
  InputProps,
  InputLabelProps,
  PopoverProps,
  CalendarProps,
  onChange,
  onClick,
  format,
  disabled,
  inputProps,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);
  const intl = useIntl();

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
    if (onClick) {
      onClick(event);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectDate = (date: LocalDate) => {
    if (onChange) {
      onChange(date);
    }
    handleClose();
  };

  const open = !!anchorEl;
  const id = open ? `date-picker-${rest.id || rest.name}` : undefined;

  const dateToFormat: LocalDate = _.isString(rest.value)
    ? LocalDate.parse(rest.value)
    : (rest.value as LocalDate);
  const calendar = (
    <CalendarV5
      css={datePickerFieldCalendar}
      onSelectDate={handleSelectDate}
      {...CalendarProps}
      selectedDate={dateToFormat}
      initialDate={dateToFormat}
    />
  );

  let value = '';
  if (dateToFormat) {
    value = time.formatDate(intl, dateToFormat, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      ...format,
      timeZone: 'UTC',
    });
  }

  return (
    <div>
      <TextField
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <KeyboardArrowDownIcon />
            </InputAdornment>
          ),
          ...InputProps,
        }}
        InputLabelProps={{
          shrink: true,
          ...InputLabelProps,
        }}
        onClick={disabled ? undefined : handleClick}
        inputProps={{ 'aria-describedby': id, ...inputProps }}
        disabled={disabled}
        {...rest}
        value={value}
      />
      <Popover
        css={datePickerFieldPopover}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        {...PopoverProps}
      >
        {calendar}
      </Popover>
    </div>
  );
};

export default DatePickerField;
